/**
 * IPPhonesList — Teléfonos IP (estándar de listados del CMDB).
 *
 * El andamiaje lo pone `common/ListPage.jsx`: buscador, filtros, exportar a
 * Excel, columnas, cebra, clic en la fila para abrir la ficha EN LECTURA y los
 * botones `[acciones propias] · Copiar · Editar · Eliminar`.
 *
 * Lo propio de un teléfono IP: sus **dos puertos** (uplink conmutador y paso a
 * PC), que se pintan como columnas y se gestionan con el mapa de puertos.
 *  · Vistas: **Lista** (por defecto) y **Fichas**.
 *  · Filtros: Estado, Ubicación, Modelo, Sala/planta y «con/sin latiguillo».
 *  · Acción propia: **Gestionar puertos**.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PhoneIcon,
  LinkIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import LocationTreeSelect from './common/LocationTreeSelect';
import { buildLocationMap, locationPath, locationPathOptions } from '../utils/locationTree';
import CiActivityPanel from './common/CiActivityPanel';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import PortMapModal from './PortMapModal';
import ElementIpPanel from './ElementIpPanel';
import { useListView } from '../views/ViewsContext';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import useNavOrder from '../hooks/useNavOrder';
import { getServerFormStyle } from '../utils/serverFormStyle';
import './ServerFormCards.css';

function IPPhonesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [phones,       setPhones]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLoc,    setFilterLoc]    = useState('');
  const [filterModel,  setFilterModel]  = useState('');
  const [filterRoom,   setFilterRoom]   = useState('');
  const [filterCable,  setFilterCable]  = useState('');
  const [hlId,         setHlId]         = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [locations,    setLocations]    = useState([]);
  // Preferencia global: ficha clásica o variante tarjetas (estándar del CMDB).
  const estiloFicha = getServerFormStyle();
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = useCallback(
    (item) => (item?.location_id ? locationPath(mapaUbicaciones, item.location_id) : '') || item?.location_name || '',
    [mapaUbicaciones],
  );
  const [showForm,     setShowForm]     = useState(false);
  const [formMode,     setFormMode]     = useState('view');
  const [editItem,     setEditItem]     = useState(null);
  const [cloneSource,  setCloneSource]  = useState(null);
  const [portMapPhone, setPortMapPhone] = useState(null);

  useEffect(() => {
    if (companyId) {
      fetchPhones();
      api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchPhones = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getIPPhones(companyId, { include_connection: true });
      setPhones(res.data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  /* ── Ficha (lectura por defecto) ─────────────────────────────────────── */
  const handleOpen = (phone) => { setCloneSource(null); setEditItem(phone); setFormMode('view'); setShowForm(true); };
  const handleEdit = (phone) => { setCloneSource(null); setEditItem(phone); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setCloneSource(null); setEditItem(null); setFormMode('edit'); setShowForm(true); };
  const handleCloseForm = () => { setShowForm(false); setEditItem(null); setCloneSource(null); fetchPhones(); };

  const handleDelete = async (phone) => {
    try {
      await api.deleteIPPhone(phone.id, companyId);
      await fetchPhones();
    } catch (err) {
      setError(err.message || tr('phone.delete_error', 'No se pudo eliminar el teléfono'));
    }
  };

  // Resalta el indicado con ?destacar=<id>[&abrir=1]
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !phones.length) return undefined;
    const id = Number(raw);
    const target = phones.find((p) => p.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { handleOpen(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`phone-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, phones, setSearchParams]);

  /* ── Puertos (uplink conmutador / paso a PC) ─────────────────────────── */
  const portOf = (phone, portNumber) => phone.ports?.find((p) => p.port_number === portNumber);
  const connectedTo = (phone, portNumber) => {
    const other = portOf(phone, portNumber)?.connection?.other_end;
    if (!other) return null;
    return `${other.element_name || '—'}${other.port_number != null ? ` P${other.port_number}` : ''}`;
  };
  const wiredPorts = (phone) => [portOf(phone, 1), portOf(phone, 2)].filter((p) => p?.connection).length;

  const modelOptions = useMemo(() => [...new Set(phones.map((p) => (p.model || '').trim()).filter(Boolean))].sort(), [phones]);
  const roomOptions = useMemo(() => [...new Set(phones.map((p) => [p.room, p.floor].filter(Boolean).join(' · ').trim()).filter(Boolean))].sort(), [phones]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return phones.filter((p) => {
      const roomText = [p.room, p.floor].filter(Boolean).join(' · ');
      const matchesSearch = !q
        || (p.code || '').toLowerCase().includes(q)
        || (p.name || '').toLowerCase().includes(q)
        || (p.room || '').toLowerCase().includes(q)
        || (p.extension || '').toLowerCase().includes(q)
        || (p.mac_address || '').toLowerCase().includes(q)
        || (p.ip_address || '').toLowerCase().includes(q)
        || (p.model || '').toLowerCase().includes(q);
      const matchesStatus = !filterStatus || (p.status || 'active') === filterStatus;
      const matchesLoc = !filterLoc || String(p.location_id) === filterLoc;
      const matchesModel = !filterModel || (p.model || '') === filterModel;
      const matchesRoom = !filterRoom || roomText === filterRoom;
      const wired = wiredPorts(p);
      const matchesCable = !filterCable
        || (filterCable === 'wired' ? wired > 0 : wired === 0);
      return matchesSearch && matchesStatus && matchesLoc && matchesModel && matchesRoom && matchesCable;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phones, search, filterStatus, filterLoc, filterModel, filterRoom, filterCable]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navPhones = useNavOrder(filtered, editItem, showForm);

  /* ── Columnas (catálogo completo; 10 visibles de entrada) ───────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    const portCell = (portNumber) => (phone) => {
      const where = connectedTo(phone, portNumber);
      return where
        ? <span className="list-badge list-badge--on">● {where}</span>
        : <span className="list-hint">○ Libre</span>;
    };
    return [
      {
        key: 'code',
        label: tr('phone.code', 'Código'),
        sortable: true,
        render: (phone) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <PhoneIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
            {phone.code ? <span className="list-code">{phone.code}</span> : dash}
          </span>
        ),
      },
      { key: 'name', label: tr('list.name', 'Nombre'), sortable: true, render: (phone) => phone.name || dash },
      {
        key: 'extension',
        label: tr('phone.extension', 'Extensión'),
        sortable: true,
        render: (phone) => (phone.extension ? <span className="list-badge">☎ {phone.extension}</span> : dash),
      },
      { key: 'model', label: tr('phone.model', 'Modelo'), sortable: true, render: (phone) => phone.model || dash },
      { key: 'mac_address', label: 'MAC', sortable: true, render: (phone) => (phone.mac_address ? <span className="list-code">{phone.mac_address}</span> : dash) },
      { key: 'ip_address', label: 'IP', sortable: true, render: (phone) => (phone.ip_address ? <span className="list-code">{phone.ip_address}</span> : dash) },
      {
        key: 'room',
        label: tr('phone.room', 'Sala / Planta'),
        sortable: true,
        sortValue: (phone) => [phone.room, phone.floor].filter(Boolean).join(' · '),
        render: (phone) => ([phone.room, phone.floor].filter(Boolean).join(' · ') || dash),
      },
      {
        key: 'location_name',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (phone) => rutaUbicacion(phone),
        render: (phone) => rutaUbicacion(phone) || dash,
      },
      { key: 'port_uplink', label: tr('phone.port_uplink', 'Puerto 1 (uplink)'), render: portCell(1) },
      { key: 'port_pc', label: tr('phone.port_pc', 'Puerto 2 (PC)'), render: portCell(2) },
      {
        key: 'status',
        label: tr('list.status', 'Estado'),
        sortable: true,
        sortValue: (phone) => phone.status || 'active',
        render: (phone) => {
          const active = (phone.status || 'active') === 'active';
          return (
            <span className={`list-badge ${active ? 'list-badge--on' : 'list-badge--off'}`}>
              {active ? tr('phone.active', 'Activo') : tr('phone.inactive', 'Inactivo')}
            </span>
          );
        },
      },
      { key: 'notes', label: tr('list.notes', 'Notas'), render: (phone) => phone.notes || dash },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, rutaUbicacion]);

  const defaultVisible = ['code', 'name', 'extension', 'model', 'ip_address', 'room', 'location_name', 'port_uplink', 'port_pc', 'status'];

  const filters = [
    {
      key: 'status',
      label: tr('phone.filter_status', 'Estado'),
      allLabel: tr('phone.filter_status_all', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('phone.active', 'Activo') },
        { value: 'inactive', label: tr('phone.inactive', 'Inactivo') },
      ],
    },
    {
      key: 'location',
      label: tr('list.filter_location', 'Ubicación'),
      allLabel: tr('list.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLoc,
      onChange: setFilterLoc,
      options: locationPathOptions(locations),
    },
    {
      key: 'model',
      label: tr('phone.filter_model', 'Modelo'),
      allLabel: tr('phone.filter_model_all', 'Todos los modelos'),
      value: filterModel,
      onChange: setFilterModel,
      options: modelOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'room',
      label: tr('phone.filter_room', 'Sala / Planta'),
      allLabel: tr('phone.filter_room_all', 'Todas las salas'),
      value: filterRoom,
      onChange: setFilterRoom,
      options: roomOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'cable',
      label: tr('phone.filter_cable', 'Latiguillo'),
      allLabel: tr('phone.filter_cable_all', 'Con y sin latiguillo'),
      value: filterCable,
      onChange: setFilterCable,
      options: [
        { value: 'wired', label: tr('phone.filter_wired', 'Con latiguillo patcheado') },
        { value: 'free', label: tr('phone.filter_unwired', 'Sin nada conectado') },
      ],
    },
  ];

  const connectedCount = phones.filter((p) => wiredPorts(p) > 0).length;
  const subtitle = `${phones.length} ${tr('phone.plural', 'teléfonos')} · ${connectedCount} ${tr('phone.connected', 'con latiguillo')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map((phone) => ({
    'Código': phone.code || '',
    'Nombre': phone.name || '',
    'Extensión': phone.extension || '',
    'Modelo': phone.model || '',
    'MAC': phone.mac_address || '',
    'IP': phone.ip_address || '',
    'Sala / Planta': [phone.room, phone.floor].filter(Boolean).join(' · '),
      'Ubicación': rutaUbicacion(phone),
    'Puerto 1 (uplink)': connectedTo(phone, 1) || 'Libre',
    'Puerto 2 (PC)': connectedTo(phone, 2) || 'Libre',
    'Estado': (phone.status || 'active') === 'active' ? 'Activo' : 'Inactivo',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [filtered]);

  /** Tarjetas: lo esencial del teléfono, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((phone) => {
          const active = (phone.status || 'active') === 'active';
          return (
            <div
              key={phone.id}
              id={`phone-card-${phone.id}`}
              className={`list-card${phone.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(phone)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(phone); }}
              title={`${tr('action.view', 'Ver')} ${phone.code}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">📞 {phone.code}</div>
                  {phone.name && <span>{phone.name}</span>}
                </div>
                <span className={`list-badge ${active ? 'list-badge--on' : 'list-badge--off'}`}>
                  {active ? tr('phone.active', 'Activo') : tr('phone.inactive', 'Inactivo')}
                </span>
              </div>

              <div className="list-card-meta">
                {phone.extension && <span className="list-badge">☎ {phone.extension}</span>}
                {phone.model && <span>{phone.model}</span>}
                {phone.ip_address && <span className="list-code">{phone.ip_address}</span>}
                {phone.mac_address && <span className="list-code">{phone.mac_address}</span>}
                {(phone.room || phone.floor) && <span>📍 {[phone.room, phone.floor].filter(Boolean).join(' · ')}</span>}
                {rutaUbicacion(phone) && <span>🏢 {rutaUbicacion(phone)}</span>}
              </div>

              <div className="list-card-meta">
                <span className={`list-badge ${connectedTo(phone, 1) ? 'list-badge--on' : ''}`}>
                  ⤒ {connectedTo(phone, 1) || tr('phone.port_free', 'Libre')}
                </span>
                <span className={`list-badge ${connectedTo(phone, 2) ? 'list-badge--on' : ''}`}>
                  ⤓ {connectedTo(phone, 2) || tr('phone.port_free', 'Libre')}
                </span>
              </div>

              <div className="list-card-actions">{rowActions(phone)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const PHONE_VIEWS = useMemo(() => [
    { key: 'list', label: tr('phone.view_list', 'Lista'), icon: ListBulletIcon, title: tr('phone.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('phone.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('phone.view_cards_hint', 'Tarjetas con lo esencial de cada teléfono'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, rutaUbicacion]);
  useListView('ip_phones', 'list');

  if (!companyId) return <div className="wol-container"><div className="alert alert-warning">Selecciona una empresa.</div></div>;

  const clonePhone = (phone) => {
    if (!phone) return;
    setCloneSource({
      ...phone,
      id: undefined,
      code: phone.code ? `${phone.code}-copia` : '',
      name: phone.name ? `${phone.name} (copia)` : '',
      status: 'active',
      ports: phone.ports || [],
    });
    setEditItem(null);
    setFormMode('edit');
    setShowForm(true);
  };

  if (!companyId) return (
    <div className="list-page" style={{ padding: '1.5rem' }}>
      <div className="alert alert-warning">{tr('phone.select_company', 'Selecciona una empresa para ver los teléfonos IP.')}</div>
    </div>
  );

  return (
    <>
      {showForm && (
        <IPPhoneForm
          item={cloneSource || editItem}
          companyId={companyId}
          locations={locations}
          initialMode={formMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navPhones}
          navIndex={editItem ? navPhones.findIndex(p => String(p.id) === String(editItem.id)) : -1}
          onNavigate={(destino) => setEditItem(destino)}
          onClose={handleCloseForm}
          onSave={(creado) => {
            fetchPhones();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setEditItem(creado); setFormMode('edit'); }
          }}
          onRefresh={fetchPhones}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={PhoneIcon}
          title={tr('phone.title', 'Teléfonos IP')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('phone.new', 'Nuevo teléfono IP')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('phone.search_placeholder', 'Buscar por código, nombre, extensión, MAC, IP, modelo…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="telefonos-ip"
          exportSheetName="Teléfonos IP"

          columns={columns}
          columnStorageKey="cmdb_cols_ip_phones"
          companyId={companyId}
          importEntity="ip-phones"
          onImported={() => fetchPhones()}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(phone) => phone.id}
          rowId={(phone) => `phone-row-${phone.id}`}
          rowClassName={(phone) => (phone.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={handleOpen}
          rowTitle={(phone) => `${tr('action.view', 'Ver')} ${phone.code}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="ip_phones"
                supportsRack={false}
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchPhones();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="ip_phones"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchPhones();
                }}
              />
            </>
          )}

          views={PHONE_VIEWS}
          listKey="ip_phones"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando teléfonos IP…')}
          error={error}

          empty={{
            icon: '☎️',
            title: phones.length === 0
              ? tr('phone.empty', 'No hay teléfonos IP registrados')
              : tr('msg.no_results', 'Ningún teléfono coincide con los filtros'),
            subtitle: phones.length === 0 ? tr('phone.empty_hint', 'Crea el primero para empezar.') : undefined,
            action: phones.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('phone.new', 'Nuevo teléfono IP')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (phone) => (
              <button
                type="button"
                className="list-action-btn"
                onClick={() => setPortMapPhone(phone)}
                title={tr('phone.ports', 'Gestionar puertos (uplink y paso a PC)')}
              >
                <LinkIcon />
              </button>
            ),
            onCopy: clonePhone,
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('phone.delete_title', 'Eliminar teléfono IP'),
            deleteMessage: (phone) => (
              <>
                {tr('phone.delete_confirm', '¿Seguro que quieres eliminar el teléfono')} <strong>{phone.code}</strong>?{' '}
                {tr('phone.delete_hint', 'Se eliminarán también sus puertos y todas sus conexiones. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {portMapPhone && (
        <PortMapModal
          element={{
            ...portMapPhone,
            element_type: 'ip_phone',
            ports: portMapPhone.ports,
          }}
          companyId={companyId}
          onClose={() => setPortMapPhone(null)}
          onUpdated={() => { setPortMapPhone(null); fetchPhones(); }}
        />
      )}
    </>
  );
}

// ── Inline form ─────────────────────────────────────────────────────────────

function IPPhoneForm({ item, companyId, locations, initialMode = 'create', onClose, onSave, onRefresh, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEdit = Boolean(item?.id);

  const [form, setForm] = useState({
    code:        item?.code        || '',
    name:        item?.name        || '',
    room:        item?.room        || '',
    floor:       item?.floor       || '',
    model:       item?.model       || '',
    mac_address: item?.mac_address || '',
    extension:   item?.extension   || '',
    notes:       item?.notes       || '',
    status:      item?.status      || 'active',
    location_id: item?.location_id ? String(item.location_id) : '',
  });
  const [saving,    setSaving]    = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [err,       setErr]       = useState('');
  const [isDirty,   setIsDirty]   = useState(false);
  // Modo lectura por defecto en las fichas existentes (estándar del CMDB).
  const [editing,   setEditing]   = useState(!isEdit || initialMode === 'edit');
  // En tarjetas la primera pestaña es siempre Resumen.
  const [activeTab, setActiveTab] = useState(isCards ? 'resumen' : 'general');
  const [ipCount,   setIpCount]   = useState(0);
  const [portsSnapshot, setPortsSnapshot] = useState(item?.ports || []);
  const [showPortModal, setShowPortModal] = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este teléfono IP',
    actionLabel: 'pasas a otro teléfono o cierras la ficha',
    save: () => handleSubmit(null, { keepEditing: editing }),
  });

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    setPortsSnapshot(item?.ports || []);
  }, [item]);

  const handleSuggest = async () => {
    try {
      setSuggesting(true);
      const res = await api.suggestName({ type: 'ip_phone', companyId, consume: true });
      const suggested = res.data?.suggested_name || res.data?.name || '';
      if (suggested) { setField('code', suggested); }
    } catch {} finally { setSuggesting(false); }
  };

  const setField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    setIsDirty(true);
    setErr('');
  };

  const handleDiscard = () => {
    if (isEdit) {
      setForm({
        code: item?.code || '', name: item?.name || '', room: item?.room || '',
        floor: item?.floor || '', model: item?.model || '',
        mac_address: item?.mac_address || '', extension: item?.extension || '',
        notes: item?.notes || '', status: item?.status || 'active',
        location_id: item?.location_id ? String(item.location_id) : '',
      });
    } else { onClose(); }
    setIsDirty(false);
    setErr('');
    // Descartar en una ficha ya guardada vuelve a LECTURA (no la cierra).
    if (isEdit) setEditing(false);
  };

  const handleSubmit = async (e, { keepEditing = false } = {}) => {
    if (e) e.preventDefault();
    if (!editing) return false;
    if (!form.code.trim()) { setErr('El código es obligatorio.'); return false; }
    setSaving(true);
    try {
      const payload = { ...form, location_id: form.location_id ? Number(form.location_id) : null };
      let creado = null;
      if (isEdit) await api.updateIPPhone(item.id, payload, companyId);
      else        creado = registroGuardado(await api.createIPPhone(payload, companyId));
      if (onRefresh) onRefresh();
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      // Una ficha existente se queda abierta EN LECTURA tras guardar (salvo
      // que se esté pasando a la siguiente con «Guardar y continuar»).
      if (isEdit && !keepEditing) setEditing(false);
      return true;
    } catch (er) { setErr(er.message || 'Error al guardar'); return false; }
    finally { setSaving(false); }
  };


  const statusStyle = form.status === 'active'
    ? { bg: '#dcfce7', color: '#15803d', label: 'Activo' }
    : { bg: '#fee2e2', color: '#991b1b', label: 'Inactivo' };

  // ── Variante tarjetas ──
  const mapaForm = useMemo(() => buildLocationMap(locations || []), [locations]);
  const rutaUbicacionForm = form.location_id ? locationPath(mapaForm, form.location_id) : '';
  const tabRed = { key: 'red', label: `Red / IP${ipCount > 0 ? ` (${ipCount})` : ''}` };
  // En tarjetas no hay pestañas «Identificación» ni «Ubicación»: se editan en Resumen.
  const tabsDef = isCards
    ? [{ key: 'resumen', label: 'General' }, { key: 'hardware', label: 'Hardware' }, tabRed]
    : [
        { key: 'general', label: 'Identificación' },
        { key: 'hardware', label: 'Hardware' },
        tabRed,
        { key: 'ubicacion', label: 'Ubicación' },
      ];
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Teléfonos IP
        </button>
        <DetailCrumb label={form.code} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Teléfono IP"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {err && <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>⚠ {err}</span>}
        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              {!editing && isEdit && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving} title="Editar la ficha">
                  <PencilSquareIcon />
                  Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
            </>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
            ● {statusStyle.label}
          </span>
          {!editing && isEdit && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
              <PencilSquareIcon />
              Editar
            </button>
          )}
          {editing && isEdit && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={!editing || saving || (isEdit && !isDirty)}
          >
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {form.extension && (
          <button className="dv-smart-btn" type="button">
            <span className="dv-smart-btn-value" style={{ color: '#0369a1' }}>☎ {form.extension}</span>
            <span className="dv-smart-btn-label">Extensión</span>
          </button>
        )}
        {form.model && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')}>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem', color: '#6b7280' }}>{form.model}</span>
            <span className="dv-smart-btn-label">Modelo</span>
          </button>
        )}
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem' }}>
            {[form.room, form.floor].filter(Boolean).join(' · ') || '—'}
          </span>
          <span className="dv-smart-btn-label">Sala / Planta</span>
        </button>
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={handleSubmit}>
          {/* En LECTURA el fieldset desactiva de golpe todos los campos, selects,
              textareas y botones internos (nativo de HTML). En tarjetas el
              fieldset exterior queda activo y el bloqueo lo hace el interior. */}
          <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<PhoneIcon style={{ width: 20, height: 20 }} />}
                title={form.code}
                onTitleChange={(v) => setField('code', v)}
                placeholder="Código del teléfono…"
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                crumb={[form.name, form.model, rutaUbicacionForm].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusStyle.label, color: form.status === 'active' ? '#16a34a' : '#dc2626' },
                  ...(form.extension ? [{ text: `Ext. ${form.extension}` }] : []),
                ]}
                chips={[
                  { value: ipCount, label: 'IPs', onClick: () => setActiveTab('red') },
                  ...(form.mac_address ? [{ value: form.mac_address, label: 'MAC' }] : []),
                  ...(form.model ? [{ value: form.model, label: 'modelo', onClick: () => setActiveTab('hardware') }] : []),
                ]}
              />
            ) : (
              <>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    className="dv-record-title"
                    value={form.code}
                    onChange={e => setField('code', e.target.value)}
                    placeholder="Código del teléfono…"
                    required
                    style={{ flex: 1 }}
                  />
                  <button type="button" title="Sugerir código" onClick={handleSuggest} disabled={suggesting}
                    style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, cursor: suggesting ? 'wait' : 'pointer', color: suggesting ? '#94a3b8' : '#7c3aed', flexShrink: 0 }}>
                    <SparklesIcon style={{ width: 18, height: 18 }} />
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsDef.map(tab => (
                  <TabButton
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset disabled={!editing} className="dv-fieldset">

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && activeTab === 'resumen' && (
            <div className={paneClass}>
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>Extensión</span><b>{form.extension || '—'}</b></div>
                <div className="sf2-kpi"><span>IPs</span><b>{ipCount}</b></div>
                <div className="sf2-kpi"><span>Puertos</span><b>{portsSnapshot?.length || 0}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusStyle.label}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código *</label>
                      {editing
                        ? <input value={form.code} onChange={e => setField('code', e.target.value)} placeholder="IP-PHONE-001" />
                        : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => setField('status', e.target.value)}>
                          <option value="active">Activo</option>
                          <option value="inactive">Inactivo</option>
                        </select>
                      ) : <div className="v">{statusStyle.label}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Nombre</label>
                      {editing
                        ? <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Teléfono sala A" />
                        : <div className="v">{form.name || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Extensión</label>
                      {editing
                        ? <input value={form.extension} onChange={e => setField('extension', e.target.value)} placeholder="2001" />
                        : <div className="v">{form.extension || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Notas</label>
                      {editing ? (
                        <textarea className="dv-input" rows={3} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Observaciones…" />
                      ) : <div className="v">{form.notes || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Ubicación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => setField('location_id', id)}
                          placeholder="— sin ubicación —"
                        />
                      ) : <div className="v">{rutaUbicacionForm || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Planta</label>
                      {editing
                        ? <input value={form.floor} onChange={e => setField('floor', e.target.value)} placeholder="Planta 3" />
                        : <div className="v">{form.floor || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Sala</label>
                      {editing
                        ? <input value={form.room} onChange={e => setField('room', e.target.value)} placeholder="Sala de reuniones 4" />
                        : <div className="v">{form.room || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Hardware{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? <input value={form.model} onChange={e => setField('model', e.target.value)} placeholder="Cisco 7841" />
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Dirección MAC</label>
                      {editing
                        ? <input value={form.mac_address} onChange={e => setField('mac_address', e.target.value)} placeholder="00:1A:2B:3C:4D:5E" />
                        : <div className="v">{form.mac_address || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Red y puertos</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Asignaciones IP</label>
                      <div className="v">{ipCount}</div>
                    </div>
                    <div className="sf2-field">
                      <label>Puertos físicos</label>
                      <div className="v">{portsSnapshot?.length || 0}</div>
                    </div>
                  </div>
                  <span className="sf2-link" role="button" tabIndex={0}
                    onClick={() => setActiveTab('red')}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setActiveTab('red'); } }}
                  >Ver IPAM y puertos →</span>
                  {isEdit && editing && (
                    <>
                      <span className="sf2-link" role="button" tabIndex={0} style={{ marginLeft: 14 }}
                        onClick={() => setShowPortModal(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowPortModal(true); } }}
                      >Gestionar puertos →</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Datos del teléfono</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.code} onChange={e => setField('code', e.target.value)} placeholder="IP-PHONE-001" required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Nombre</label>
                    <input className="form-control" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Teléfono sala A" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Extensión</label>
                    <input className="form-control" value={form.extension} onChange={e => setField('extension', e.target.value)} placeholder="2001" />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'hardware' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Hardware</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Modelo</label>
                    <input className="form-control" value={form.model} onChange={e => setField('model', e.target.value)} placeholder="Cisco 7841" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Dirección MAC</label>
                    <input className="form-control" value={form.mac_address} onChange={e => setField('mac_address', e.target.value)} placeholder="00:1A:2B:3C:4D:5E" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'red' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">IPAM — Asignaciones de IP</div>
                <ElementIpPanel
                  objectType="ip_phone"
                  objectId={item?.id}
                  companyId={companyId}
                  elementName={item?.code || form.code}
                  onCountChange={setIpCount}
                />
              </div>

              {isEdit && (
                <div className="dv-section">
                  <div className="dv-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Puertos físicos</span>
                    <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setShowPortModal(true)}>
                      <LinkIcon /> Gestionar puertos
                    </button>
                  </div>
                  <div className="row g-3">
                    {portsSnapshot?.length ? portsSnapshot.map(p => (
                      <div key={p.id || p.port_number} className="col-md-6">
                        <div className="card" style={{ border: '1px solid #e2e8f0', borderRadius: 10 }}>
                          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
                            <div>
                              <div style={{ fontWeight: 700 }}>Puerto #{p.port_number}</div>
                              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                {p.connection ? (
                                  <>
                                    ● Conectado a {p.connection.other_end?.element_name || '—'}
                                    {p.connection.other_end?.port_number ? ` · P${p.connection.other_end.port_number}` : ''}
                                  </>
                                ) : '○ Libre'}
                              </div>
                              {p.connection?.cable_type && (
                                <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>[{p.connection.cable_type}] {p.connection.cable_color || ''}</div>
                              )}
                            </div>
                            <button type="button" className="wol-action-btn" title="Editar puerto" onClick={() => setShowPortModal(true)}>
                              <LinkIcon />
                            </button>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="col-12" style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Sin puertos registrados.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ubicacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Sala</label>
                    <input className="form-control" value={form.room} onChange={e => setField('room', e.target.value)} placeholder="Sala de reuniones 4" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Planta</label>
                    <input className="form-control" value={form.floor} onChange={e => setField('floor', e.target.value)} placeholder="Planta 3" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ubicación</label>
                    <LocationTreeSelect
                      locations={locations}
                      value={form.location_id}
                      onChange={(id) => setField('location_id', id)}
                      placeholder="— sin ubicación —"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null)}
          />
          </FormCardsShell>
          </fieldset>
        </form>

        {/* ── Right sidebar ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="ip_phone"
          ciId={item?.id}
          tipoLegible="el teléfono"
        />
      </div>

      {showPortModal && isEdit && (
        <PortMapModal
          element={{ ...(item || {}), element_type: 'ip_phone', ports: portsSnapshot }}
          companyId={companyId}
          onClose={() => setShowPortModal(false)}
          onUpdated={async () => {
            if (onRefresh) onRefresh();
            try {
              const res = await api.getIPPhones(companyId, { include_connection: true });
              const updated = (res.data || []).find(p => p.id === item.id);
              if (updated) setPortsSnapshot(updated.ports || []);
            } catch (_) { /* ignore */ }
            setShowPortModal(false);
          }}
        />
      )}
    </div>
  );
}

export default IPPhonesList;
